import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";

import PropTypes from "prop-types";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import Swal from "sweetalert2";
import { Box, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton } from "@mui/material";
import SoftInput from "components/SoftComponents/SoftInput";
import api from "services/api/translations";
import theme from "assets/theme";
import { useSoftUIController } from "hooks/context";
import RevisedTable from "components/Tables/RevisedTable";

function Blacklist({ translationTypes, selectedType, cautionView, setCautionView }) {

    api.loadToken(useAuth0());
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, appearance } = controller;

    const queryClient = useQueryClient();

    // --- STATE ---
    const [searchQuery, setSearchQuery] = useState("");

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null); // Null if adding new, object if editing
    const [formData, setFormData] = useState({ input: "", output: "" });

    // --- QUERIES ---
    const translations = useQuery({
        queryKey: ['translations', selectedOrg],
        queryFn: () => api.getTranslations(),
        staleTime: 5 * 60 * 1000,
    });

    // --- MUTATIONS ---
    const saveMutation = useMutation({
        mutationFn: (data) => editingItem
            ? api.updateTranslation(editingItem._id, data)
            : api.createTranslation(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['translations', selectedOrg] });
            closeModal();
            Swal.fire({
                icon: 'success',
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Saved!</div>',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        },
        onError: (error) => {
            Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${error.message || "Failed to save translation. Ensure the input is unique."}</div>`, "error");
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => api.deleteTranslation(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['translations', selectedOrg] });
            Swal.fire('<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Deleted!</div>', '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">The translation has been removed.</div>', 'success');
        }
    });

    // --- HANDLERS ---
    const handleAddClick = () => {
        if (!selectedType) return Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Select a Type</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Please select a translation type first.</div>`, "info");
        setEditingItem(null);
        setFormData({ input: "", output: "", translationType: selectedType });
        setIsModalOpen(true);
    };

    const handleEditClick = (item) => {
        setEditingItem(item);
        setFormData({ input: item.input, output: item.output, translationType: item.translationType });
        setIsModalOpen(true);
    };

    const handleDeleteClick = (item) => {
        Swal.fire({
            title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Are you sure?</div>',
            html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">You are about to delete the translation for "${item.input}".</div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#cb0c9f',
            cancelButtonColor: '#8392ab',
            confirmButtonText: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Yes, delete it!</div>'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMutation.mutate(item._id);
            }
        });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData({ input: "", output: "" });
    };

    // --- FILTERING ---
    const filteredTranslations = translations.data?.filter(t => {
        const matchesType = t.translationType === selectedType;
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = t.input.toLowerCase().includes(searchLower) || t.output.toLowerCase().includes(searchLower);
        return matchesType && matchesSearch;
    }) || [];

    // --- SAVE HANDLING ---
    const handleSave = () => {
        const input = !formData.input.trim();
        if (input) return Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Required</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Both Input and Output are required.</div>`, "warning");

        saveMutation.mutate(formData);
    };

    const data_table_data = {
        columns: [
            {
                Header: 'Input Text',
                accessor: 'input',
            },
            {
                Header: 'Output Translation',
                accessor: 'output',
                weight: 'medium',
            },
            {
                Header: 'Actions',
                accessor: 'translationType',
                align: 'right',
                width: '50px',
                Cell: ({ cell }) => (
                    <Box display='flex' flexDirection={'row'} gap={deviceLayout == 'mobile' ? 0.5 : 1}>
                        <IconButton size="small" color="secondary" onClick={() => handleEditClick(cell.row.original)}>
                            <Icon>edit</Icon>
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(cell.row.original)}>
                            <Icon>delete</Icon>
                        </IconButton>
                    </Box>
                )
            }
        ],
        rows: filteredTranslations
    };

    if (cautionView) {
        return (
            <SoftBox p={1} sx={{
                height: deviceLayout == 'mobile' ? 'unset' : 'calc(100svh - 11.3rem - 16px)',
                overflowY: 'auto',
                width: deviceLayout == 'mobile' ? 'unset' : '100%',
            }}>
                <SoftBox height={deviceLayout == 'mobile' ? "calc(100svh - 8.4rem - 16px)" : "100%"} display="flex" flexDirection="column" justifyContent='center' alignItems="center" sx={{ backgroundColor: '#888', borderRadius: '10px' }}>
                    <SoftBox p={4} m={4} sx={{ maxWidth: '500px', borderRadius: '20px', backgroundColor: '#eee' }}>
                        <SoftBox display="flex" flexDirection="column" justifyContent='center' alignItems="center">
                            <SoftTypography pb={2} variant="d5" fontWeight="bold">CAUTION</SoftTypography>
                            <SoftTypography variant="subtitle2" sx={{ textAlign: 'center' }}>The following content may be considered profane, vulgar, or offensive to some users. Continue at your own discretion.</SoftTypography>
                        </SoftBox>
                        <SoftBox display="flex" justifyContent="center">
                            <SoftButton variant="gradient" color="warning" sx={{ mt: '60px' }} onClick={() => { setCautionView(false) }}>
                                PROCEED
                            </SoftButton>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        );
    }
    else {
        return (
            <>
                <SoftBox m={1} display="flex" sx={{ width: 'calc(100% - 16px)' }}>
                    <SoftBox display="flex" flexDirection="column" color={"white"} width="100%"
                        sx={({ functions: { rgba } }) => ({
                            backgroundColor: rgba("#fff", 1),
                            overflow: "hidden",
                            minWidth: 0,
                            height: `calc(100% - 16px)`
                        })}
                        px={{ xxs: 2, lg: 3 }} pb={{ xxs: 2, lg: 3 }} pt={3}
                    >
                        <SoftBox width="100%" pt={{ xxs: 2, md: 0 }} pb={{ xxs: 3, md: 0 }}>
                            <SoftTypography variant="h4" fontWeight="bold" color={appearance}>Translations Blacklist</SoftTypography>
                            <SoftTypography variant="button" fontWeight="regular" color="text">For replacing sensitive content</SoftTypography>
                        </SoftBox>
                        <RevisedTable noBackground table={data_table_data} canAdd={{ state: true, func: handleAddClick }} canSearch>
                            {(translations.isLoading || translationTypes.isLoading) ? (
                                <SoftBox display="flex" justifyContent="center" alignItems="center" p={5}>
                                    <CircularProgress color="inherit" />
                                </SoftBox>
                            ) : filteredTranslations.length === 0 ? (
                                <SoftBox display="flex" justifyContent="center" alignItems="center" p={5} flexDirection="column">
                                    <Icon fontSize="large" sx={{ color: '#aaa', mb: 1 }}>translate</Icon>
                                    <SoftTypography variant="button" color="text" fontWeight="medium" textAlign="center">
                                        No translations found for this category.
                                    </SoftTypography>
                                </SoftBox>
                            ) : null}
                        </RevisedTable>
                    </SoftBox>
                </SoftBox>

                {/* Add / Edit Dialog */}
                <Dialog open={isModalOpen} onClose={closeModal} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '1rem' } }}>
                    <DialogTitle>
                        <SoftTypography variant="h6" fontWeight="bold">
                            {editingItem ? "Edit Translation" : "Add New Translation"}
                        </SoftTypography>
                    </DialogTitle>
                    <DialogContent>
                        <SoftBox pt={1} pb={2}>
                            <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Original Input</SoftTypography>
                            <SoftInput
                                placeholder="e.g., Central Station"
                                value={formData.input}
                                onChange={(e) => setFormData({ ...formData, input: e.target.value })}
                                disabled={saveMutation.isPending}
                            />
                        </SoftBox>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, pt: 0 }}>
                        <SoftButton onClick={closeModal} variant="text" color="secondary" disabled={saveMutation.isPending}>
                            Cancel
                        </SoftButton>
                        <SoftButton onClick={handleSave} variant="gradient" color="dark" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                        </SoftButton>
                    </DialogActions>
                </Dialog>
            </>
        );
    }
}

Blacklist.propTypes = {
    translationTypes: PropTypes.object,
    selectedType: PropTypes.string,
    cautionView: PropTypes.bool,
    setCautionView: PropTypes.func
}

export default Blacklist;